import React, { useCallback, useEffect, useRef, useState } from 'react'
import { CanonicalLoad, LoadStop } from '@numeo/types-core'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import { cn } from 'utils/cn'
import { PrivateLoadMapSection } from './PrivateLoadMapSection'
import { useLoadDetailTab } from '../../hooks/useLoadDetailTab'
import { LocationData } from '../../types/filters'
import { ConvoyDetailStop, ConvoyOfferDetail } from '../../api/convoy.api'

/** Format date/time for display (MM/DD HH:MM AM/PM) using UTC to avoid timezone shift */
const formatDateTime = (date: Date | string | null): string => {
    if (!date) return 'TBD'
    const d = typeof date === 'string' ? new Date(date) : date
    if (isNaN(d.getTime())) return 'TBD'
    const month = d.getUTCMonth() + 1
    const day = d.getUTCDate()
    const hours = d.getUTCHours()
    const minutes = d.getUTCMinutes().toString().padStart(2, '0')
    const hours12 = hours % 12 || 12
    const ampm = hours >= 12 ? 'PM' : 'AM'
    return `${month}/${day} ${hours12.toString().padStart(2, '0')}:${minutes}${ampm}`
}

/** Get timezone abbreviation from IANA timezone (e.g. "America/Chicago" → "CT") */
const getTzAbbr = (tz: string | null | undefined): string | null => {
    if (!tz) return null
    try {
        const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date())
        return parts.find((p) => p.type === 'timeZoneName')?.value ?? null
    } catch {
        return null
    }
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const TZ_ABBR: Record<string, string> = {
    'America/New_York': 'EDT',
    'America/Chicago': 'CDT',
    'America/Denver': 'MDT',
    'America/Los_Angeles': 'PDT',
    'America/Detroit': 'EDT',
    'America/Indiana/Indianapolis': 'EDT',
    'America/Phoenix': 'MST',
    'US/Eastern': 'EDT',
    'US/Central': 'CDT',
    'US/Mountain': 'MDT',
    'US/Pacific': 'PDT',
}

/** Format Emerge stop schedule like "Tue, Mar. 17, 2026 06:30 - 13:30 (CDT)" */
const formatEmergeSchedule = (stop: LoadStop): { dateLine: string; typeLine: string } => {
    const start = stop.stopWindowStart ? (typeof stop.stopWindowStart === 'string' ? new Date(stop.stopWindowStart) : stop.stopWindowStart) : null
    const end = stop.stopWindowEnd ? (typeof stop.stopWindowEnd === 'string' ? new Date(stop.stopWindowEnd) : stop.stopWindowEnd) : null
    if (!start || isNaN(start.getTime())) return { dateLine: 'TBD', typeLine: '' }

    const day = DAYS[start.getUTCDay()]
    const month = MONTHS[start.getUTCMonth()]
    const date = start.getUTCDate()
    const year = start.getUTCFullYear()
    const startTime = `${String(start.getUTCHours()).padStart(2, '0')}:${String(start.getUTCMinutes()).padStart(2, '0')}`
    const endTime = end && !isNaN(end.getTime()) ? `${String(end.getUTCHours()).padStart(2, '0')}:${String(end.getUTCMinutes()).padStart(2, '0')}` : null
    const tzAbbr = stop.stopTimezone ? TZ_ABBR[stop.stopTimezone] || stop.stopTimezone : ''

    const timeStr = endTime ? `${startTime} - ${endTime}` : startTime
    const dateLine = `${day}, ${month}. ${date}, ${year} ${timeStr}${tzAbbr ? ` (${tzAbbr})` : ''}`
    const typeLine = stop.stopType === 'PICKUP' ? 'Requested Pick up' : 'Requested Drop off'
    return { dateLine, typeLine }
}

/** Get stop label like "Pick up 1" or "Drop off 2" */
const getStopLabel = (stops: LoadStop[], idx: number): string => {
    const stop = stops[idx]
    const isPickup = stop.stopType === 'PICKUP'
    const prefix = isPickup ? 'Pick up' : 'Drop off'
    let count = 0
    for (let i = 0; i <= idx; i++) {
        if (stops[i].stopType === stop.stopType) count++
    }
    return `${prefix} ${count}`
}

interface TripScheduleSectionProps {
    load: CanonicalLoad
    detailStops: LoadStop[] | null
    loadDetailLoading?: boolean
    originLocation?: LocationData | null
    onLoadUpdate?: (updatedLoad: CanonicalLoad) => void
    /** Convoy-specific full offer detail — when present, the Trip view renders rich
     * stop blocks (street address, facility rating, time window) instead of the
     * generic pickup/dropoff lines. Lifted from LoadExpandedRow's `useConvoyOfferDetail`. */
    convoyDetail?: ConvoyOfferDetail | null
    onDetailToolOpened?: (tool: 'trip' | 'map') => void
}

/** Format a Convoy stop's full window in the stop's IANA timezone — "Mon 4/27 06:00–21:00 CDT". */
function formatConvoyStopWindow(stop: ConvoyDetailStop): string {
    if (!stop.driverStartTime) return 'TBD'
    const tz = stop.timezone || 'UTC'
    try {
        const start = new Date(stop.driverStartTime)
        const end = stop.driverEndTime ? new Date(stop.driverEndTime) : null
        const dateStr = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            weekday: 'short',
            month: 'numeric',
            day: 'numeric',
        }).format(start)
        const timeOpts: Intl.DateTimeFormatOptions = { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false }
        const startTime = new Intl.DateTimeFormat('en-US', timeOpts).format(start)
        const endTime = end ? new Intl.DateTimeFormat('en-US', timeOpts).format(end) : null
        const tzAbbr = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
            .formatToParts(start)
            .find((p) => p.type === 'timeZoneName')?.value
        const timeRange = endTime && endTime !== startTime ? `${startTime}–${endTime}` : startTime
        return `${dateStr} ${timeRange}${tzAbbr ? ` ${tzAbbr}` : ''}`
    } catch {
        return stop.driverStartTime
    }
}

/** Render one rich Convoy stop — type label, numbered marker, city/state, street, facility rating, time window. */
const ConvoyStopBlock: React.FC<{ stop: ConvoyDetailStop; index: number }> = ({ stop, index }) => {
    const isPickup = stop.type === 'PICKUP'
    const isLive = stop.loadingType === 'LIVE'
    const label = isPickup ? (isLive ? 'Live Pickup' : 'Pickup') : isLive ? 'Live Unload' : 'Drop off'
    const accentColor = isPickup ? 'var(--lh-accent)' : 'var(--lh-success)'
    const accentBg = isPickup ? 'var(--lh-accent-soft)' : 'var(--lh-success-soft)'

    const addr = stop.address
    const cityState = addr?.city && addr?.state ? `${addr.city}, ${addr.state}` : '—'
    const street = addr?.full || (addr?.streetNumber && addr?.street ? `${addr.streetNumber} ${addr.street}` : null)
    const window = formatConvoyStopWindow(stop)
    const rating = stop.facilityRating

    return (
        <div className="flex items-stretch gap-3">
            <div className="flex flex-col items-center w-4 pt-1">
                <span
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-bold text-white"
                    style={{ background: accentColor }}
                >
                    {index + 1}
                </span>
            </div>
            <div className="flex-1 min-w-0">
                <span className="inline-block text-[10px] font-medium px-1.5 py-0.5 rounded mb-1" style={{ background: accentBg, color: accentColor }}>
                    {label}
                </span>
                <div className="text-[12px] font-semibold text-[var(--lh-fg)]">{cityState}</div>
                {street && <div className="text-[11px] text-[var(--lh-fg-muted)] mt-0.5">{street}</div>}
                {stop.locationName && stop.locationName !== street && (
                    <div className="text-[11px] text-[var(--lh-fg-muted)] mt-0.5 flex items-center gap-1">
                        {rating?.averageScore != null && <span className="text-[var(--lh-warning)] font-semibold">{rating.averageScore.toFixed(1)}★</span>}
                        <span>{stop.locationName}</span>
                    </div>
                )}
                <div className="text-[11px] text-[var(--lh-fg-muted)] mt-0.5">{window}</div>
            </div>
        </div>
    )
}

export const TripScheduleSection: React.FC<TripScheduleSectionProps> = ({
    load,
    detailStops,
    loadDetailLoading,
    originLocation,
    onLoadUpdate,
    convoyDetail,
    onDetailToolOpened,
}) => {
    // Remembered across loads + sessions (see useLoadDetailTab): open a new load on
    // the tab last used instead of always resetting to Trip.
    const [detailTab, setDetailTab] = useLoadDetailTab()
    const [miles, setMiles] = useState(() => (load.distanceMiles ? String(Math.round(Number(load.distanceMiles))) : ''))
    const [dh, setDh] = useState(() => {
        const total = (load.details?.origin_deadhead_miles ?? 0) + (load.details?.destination_deadhead_miles ?? 0)
        return total > 0 ? String(Math.round(total)) : ''
    })
    const [rateStr, setRateStr] = useState(() => (load.rateAmountUsd ? String(Math.round(Number(load.rateAmountUsd))) : ''))

    // Update DH when load.details.origin_deadhead_miles changes (e.g. calculated from map)
    const prevDhRef = useRef(load.details?.origin_deadhead_miles)
    useEffect(() => {
        const newDhO = load.details?.origin_deadhead_miles
        if (newDhO != null && newDhO !== prevDhRef.current) {
            prevDhRef.current = newDhO
            const total = newDhO + (load.details?.destination_deadhead_miles ?? 0)
            setDh(String(Math.round(total)))
        }
    }, [load.details?.origin_deadhead_miles, load.details?.destination_deadhead_miles])

    const milesNum = parseFloat(miles) || 0
    const dhNum = parseFloat(dh) || 0
    const rateNum = parseFloat(rateStr) || 0
    const totalWithDH = milesNum + dhNum
    const rpm = rateNum && milesNum > 0 ? (rateNum / milesNum).toFixed(2) : null
    const rpmWithDH = rateNum && totalWithDH > 0 ? (rateNum / totalWithDH).toFixed(2) : null

    const handleInput = useCallback(
        (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const v = e.target.value.replace(/[^0-9.]/g, '')
            setter(v)
        },
        []
    )

    const isEmerge = load.sourceType === 'EMERGE'
    const isSchneider = load.sourceType === 'SCHNEIDER'
    const isEcho = load.sourceType === 'ECHO'
    const directStops = (isSchneider || isEcho) && load.stops?.length ? (load.stops as LoadStop[]) : null

    return (
        <div className="w-full h-full min-w-0 flex flex-col overflow-auto gap-2 px-1 py-1">
            {/* Segmented Trip/Map tab selector — reference line 8299-8340 */}
            <div className="flex p-[2px] rounded-full bg-[var(--lh-border)] gap-[2px] shrink-0">
                <button
                    type="button"
                    onClick={() => {
                        if (detailTab !== 'trip') onDetailToolOpened?.('trip')
                        setDetailTab('trip')
                    }}
                    className={cn(
                        'flex-1 flex items-center justify-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-medium transition-all border-none cursor-pointer',
                        detailTab === 'trip'
                            ? 'bg-[var(--lh-surface)] text-[var(--lh-fg)] shadow-[0_2px_8px_rgba(0,0,0,0.06)]'
                            : 'bg-transparent text-[var(--lh-fg-muted)]'
                    )}
                >
                    Trip
                </button>
                <button
                    type="button"
                    onClick={() => {
                        if (detailTab !== 'map') onDetailToolOpened?.('map')
                        setDetailTab('map')
                    }}
                    className={cn(
                        'flex-1 flex items-center justify-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-medium transition-all border-none cursor-pointer',
                        detailTab === 'map'
                            ? 'bg-[var(--lh-surface)] text-[var(--lh-fg)] shadow-[0_2px_8px_rgba(0,0,0,0.06)]'
                            : 'bg-transparent text-[var(--lh-fg-muted)]'
                    )}
                >
                    Map
                </button>
            </div>

            {detailTab === 'map' ? (
                <div
                    className="flex-1 min-h-[160px] rounded-xl border border-[var(--lh-border)] bg-[var(--lh-surface-secondary)] overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    <PrivateLoadMapSection
                        load={load}
                        originLocation={originLocation}
                        onLoadUpdate={onLoadUpdate}
                        detailStops={detailStops}
                        convoyStops={convoyDetail?.shipment?.stops ?? null}
                    />
                </div>
            ) : (
                <>
                    <div className="flex-1 flex flex-col">
                        {(isSchneider || isEcho) && directStops && directStops.length > 0 ? (
                            <div className="flex flex-col gap-3">
                                {directStops.map((stop, idx) => {
                                    const isLast = idx === directStops.length - 1
                                    const isPickup = stop.stopType === 'PICKUP'
                                    const StopIcon = isLast ? FlagOutlinedIcon : RadioButtonUncheckedIcon
                                    const tzAbbr = stop.stopTimezone ? TZ_ABBR[stop.stopTimezone] || getTzAbbr(stop.stopTimezone) || stop.stopTimezone : null
                                    return (
                                        <div key={idx} className="flex items-stretch gap-3">
                                            <div className="flex flex-col items-center w-4 flex-shrink-0">
                                                <StopIcon
                                                    style={{ fontSize: 16 }}
                                                    className={isPickup ? 'text-[var(--lh-accent)]' : 'text-[var(--lh-success)]'}
                                                />
                                                {!isLast && <div className="w-px flex-1 bg-[var(--lh-border)] my-0.5" />}
                                            </div>
                                            <div className="flex-1 min-w-0 pb-1">
                                                <span
                                                    className={cn(
                                                        'inline-block text-[10px] font-medium px-1.5 py-0.5 rounded mb-1',
                                                        isPickup
                                                            ? 'bg-[var(--lh-accent-soft)] text-[var(--lh-accent)]'
                                                            : 'bg-[var(--lh-success-soft)] text-[var(--lh-success)]'
                                                    )}
                                                >
                                                    {getStopLabel(directStops, idx)}
                                                </span>
                                                <div className="text-[12px] font-medium text-[var(--lh-fg)]">
                                                    {stop.stopCity}, {stop.stopStateProv} {stop.stopPostalCode || ''}
                                                </div>
                                                <div className="text-[12px] text-[var(--lh-fg-muted)] mt-0.5">
                                                    {formatDateTime(stop.stopWindowStart)}
                                                    {stop.stopWindowEnd && <span> — {formatDateTime(stop.stopWindowEnd)}</span>}
                                                    {tzAbbr && <span className="ml-1">{tzAbbr}</span>}
                                                </div>
                                                {stop.stopFacilityName && (
                                                    <div className="text-[11px] text-[var(--lh-fg-muted)] mt-0.5 truncate">{stop.stopFacilityName}</div>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        ) : (isEmerge || load.sourceType === 'BEON') && loadDetailLoading ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="flex items-center gap-2 text-[12px] text-[var(--lh-fg-muted)]">
                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                    Loading details...
                                </div>
                            </div>
                        ) : isEmerge && detailStops && detailStops.length > 0 ? (
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-[12px] font-medium text-[var(--lh-fg)]">Stops</span>
                                    <span className="text-[11px] bg-[var(--lh-surface-secondary)] text-[var(--lh-fg-muted)] px-1.5 py-0.5 rounded">
                                        {detailStops.length}
                                    </span>
                                </div>
                                <table className="w-full text-[11px]">
                                    <thead>
                                        <tr className="bg-[var(--lh-surface-secondary)]">
                                            <th className="text-left font-medium text-[var(--lh-fg-muted)] px-1.5 py-1">#</th>
                                            <th className="text-left font-medium text-[var(--lh-fg-muted)] px-1.5 py-1">Location</th>
                                            <th className="text-left font-medium text-[var(--lh-fg-muted)] px-1.5 py-1">Appt</th>
                                            <th className="text-left font-medium text-[var(--lh-fg-muted)] px-1.5 py-1">Load</th>
                                            <th className="text-left font-medium text-[var(--lh-fg-muted)] px-1.5 py-1">Schedule</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detailStops.map((stop, idx) => {
                                            const { dateLine, typeLine } = formatEmergeSchedule(stop)
                                            const location = [stop.stopCity, `${stop.stopStateProv || ''} ${stop.stopPostalCode || ''}`.trim()]
                                                .filter(Boolean)
                                                .join(', ')
                                            return (
                                                <tr key={idx} className="border-t border-[var(--lh-border)]">
                                                    <td className="px-1.5 py-2 text-[var(--lh-fg-muted)]">{stop.stopSequence}</td>
                                                    <td className="px-1.5 py-2 text-[var(--lh-fg)] whitespace-nowrap">{location}</td>
                                                    <td className="px-1.5 py-2 text-[var(--lh-fg-muted)]">{stop.stopAppointment || '-'}</td>
                                                    <td className="px-1.5 py-2 text-[var(--lh-fg-muted)]">{stop.stopFacilityName || '-'}</td>
                                                    <td className="px-1.5 py-2">
                                                        <div className="text-[var(--lh-fg)] whitespace-nowrap">{dateLine}</div>
                                                        {typeLine && <div className="text-[var(--lh-fg-muted)] text-[10px]">{typeLine}</div>}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : detailStops && detailStops.length > 0 ? (
                            <div className="flex flex-col gap-3">
                                {detailStops.map((stop, idx) => {
                                    const isLast = idx === detailStops.length - 1
                                    const isPickup = stop.stopType === 'PICKUP'
                                    const StopIcon = isLast ? FlagOutlinedIcon : RadioButtonUncheckedIcon
                                    const location = [
                                        stop.stopCity || (isPickup ? load.pickupCity : load.deliveryCity),
                                        stop.stopStateProv || (isPickup ? load.pickupStateProv : load.deliveryStateProv),
                                    ]
                                        .filter(Boolean)
                                        .join(', ')

                                    const stopDate =
                                        stop.stopWindowStart ||
                                        (isPickup ? (load.pickupDateTime ?? load.pickupWindowStart) : (load.deliveryDateTime ?? load.deliveryWindowStart))

                                    return (
                                        <div key={idx} className="flex items-stretch gap-3">
                                            <div className="flex flex-col items-center w-4 flex-shrink-0">
                                                <StopIcon style={{ fontSize: 16 }} className="text-[var(--lh-fg-muted)]" />
                                                {!isLast && <div className="w-px flex-1 bg-[var(--lh-border)] my-0.5" />}
                                            </div>
                                            <div className="flex-1 min-w-0 pb-2">
                                                <span
                                                    className={cn(
                                                        'inline-block text-[10px] font-medium px-1.5 py-0.5 rounded mb-1',
                                                        isPickup
                                                            ? 'bg-[var(--lh-accent-soft)] text-[var(--lh-accent)]'
                                                            : 'bg-[var(--lh-success-soft)] text-[var(--lh-success)]'
                                                    )}
                                                >
                                                    {isPickup ? 'Pick up' : 'Drop off'}
                                                </span>
                                                <div className="text-[12px] font-medium text-[var(--lh-fg)]">{location || 'Unknown'}</div>

                                                {/* Facility name + rating */}
                                                {stop.stopFacilityName && (
                                                    <div className="text-[11px] text-[var(--lh-fg-muted)] mt-0.5">
                                                        {stop.stopFacilityName}
                                                        {stop.stopFacilityRating && (
                                                            <span className="ml-1">
                                                                · {stop.stopFacilityRating} <span className="text-amber-400">★</span>
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                                {/* Street address */}
                                                {stop.stopFormattedAddress && (
                                                    <div className="text-[11px] text-[var(--lh-fg-muted)]">{stop.stopFormattedAddress}</div>
                                                )}

                                                <div className="mt-1 text-[12px] text-[var(--lh-fg-muted)]">
                                                    <span>{isPickup ? 'Ship: ' : 'Delivery: '}</span>
                                                    <span className="font-semibold text-[var(--lh-fg)]">
                                                        {stop.stopTimezone && stopDate
                                                            ? new Intl.DateTimeFormat('en-US', {
                                                                  month: 'short',
                                                                  day: 'numeric',
                                                                  hour: 'numeric',
                                                                  minute: '2-digit',
                                                                  timeZone: stop.stopTimezone,
                                                                  timeZoneName: 'short',
                                                              }).format(stopDate instanceof Date ? stopDate : new Date(stopDate))
                                                            : stop.stopDateFormatted || (stopDate ? formatDateTime(stopDate) : 'TBD')}
                                                    </span>
                                                </div>

                                                {stop.stopHours && !stop.stopAppointment && (
                                                    <div className="mt-0.5 text-[12px] text-[var(--lh-fg-muted)]">
                                                        <span>{isPickup ? 'Loading: ' : 'Unloading: '}</span>
                                                        <span className="font-semibold text-[var(--lh-fg)]">{stop.stopHours}</span>
                                                    </div>
                                                )}

                                                {(stop.stopAppointment || stop.stopAppointmentType) && (
                                                    <div className="mt-0.5 text-[12px] text-[var(--lh-fg-muted)]">
                                                        <span>Appointment: </span>
                                                        <span className="font-semibold text-[var(--lh-fg)]">
                                                            {stop.stopAppointment || stop.stopAppointmentType}
                                                        </span>
                                                    </div>
                                                )}

                                                {stop.stopInstructions && (
                                                    <div className="mt-2 text-[12px]">
                                                        <div className="font-semibold text-[var(--lh-fg)] mb-0.5">Notes</div>
                                                        <div className="text-[var(--lh-fg-muted)] whitespace-pre-line text-[11px] leading-relaxed max-h-[120px] overflow-y-auto">
                                                            {stop.stopInstructions}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        ) : convoyDetail?.shipment?.stops && convoyDetail.shipment.stops.length > 0 ? (
                            // Convoy-specific: render rich stop blocks (street, locationName, facility
                            // rating, full time window) sourced from `OfferPage`. Replaces the generic
                            // pickup/drop-off below for Convoy loads only.
                            <div className="flex flex-col gap-3">
                                {convoyDetail.shipment.stops
                                    .slice()
                                    .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
                                    .map((stop, idx) => (
                                        <ConvoyStopBlock key={stop.id} stop={stop} index={idx} />
                                    ))}
                            </div>
                        ) : (
                            <div className="flex flex-col gap-3">
                                {/* Pickup stop */}
                                <div className="flex items-stretch gap-3">
                                    <div className="flex flex-col items-center w-4 pt-1">
                                        <span className="w-2 h-2 rounded-full bg-[var(--lh-accent)]" />
                                        <div className="w-px flex-1 min-h-[16px] bg-[var(--lh-border)] my-1" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <span className="inline-block text-[10px] font-medium px-1.5 py-0.5 rounded bg-[var(--lh-accent-soft)] text-[var(--lh-accent)] mb-1">
                                            Pick up
                                        </span>
                                        <div className="text-[12px] font-medium text-[var(--lh-fg)]">
                                            {formatDateTime(load.pickupDateTime ?? load.pickupWindowStart)}
                                            {/* Show window end when distinct from start (Convoy gives a 6h pickup window:
                                             * "07:00–13:30 CDT") so we render "4/27 07:00AM – 01:30PM" instead of just one time. */}
                                            {load.pickupWindowEnd &&
                                                load.pickupWindowStart &&
                                                new Date(load.pickupWindowEnd).getTime() !== new Date(load.pickupWindowStart).getTime() && (
                                                    <span> – {formatDateTime(load.pickupWindowEnd)}</span>
                                                )}
                                            {load.pickupTimezone && (
                                                <span className="text-[11px] text-[var(--lh-fg-muted)] ml-1">{getTzAbbr(load.pickupTimezone)}</span>
                                            )}
                                        </div>
                                        <div className="text-[12px] text-[var(--lh-fg-muted)] mt-0.5">
                                            {load.pickupCity}, {load.pickupStateProv}
                                        </div>
                                    </div>
                                </div>

                                {/* Delivery stop */}
                                <div className="flex items-stretch gap-3">
                                    <div className="flex flex-col items-center w-4 pt-1">
                                        <span className="w-2 h-2 rounded-full border-2 border-[var(--lh-success)]" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <span className="inline-block text-[10px] font-medium px-1.5 py-0.5 rounded bg-[var(--lh-success-soft)] text-[var(--lh-success)] mb-1">
                                            Drop off
                                        </span>
                                        <div className="text-[12px] font-medium text-[var(--lh-fg)]">
                                            {formatDateTime(load.deliveryDateTime || load.deliveryWindowStart)}
                                            {load.deliveryWindowEnd &&
                                                load.deliveryWindowStart &&
                                                new Date(load.deliveryWindowEnd).getTime() !== new Date(load.deliveryWindowStart).getTime() && (
                                                    <span> – {formatDateTime(load.deliveryWindowEnd)}</span>
                                                )}
                                            {load.deliveryTimezone && (
                                                <span className="text-[11px] text-[var(--lh-fg-muted)] ml-1">{getTzAbbr(load.deliveryTimezone)}</span>
                                            )}
                                        </div>
                                        <div className="text-[12px] text-[var(--lh-fg-muted)] mt-0.5">
                                            {load.deliveryCity}, {load.deliveryStateProv}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Miles / DH / Rate — compact rate calculator grid (minimized). Hidden for UBER_FREIGHT (shown in col 3). */}
                    <div className={cn('pt-2 border-t border-[var(--lh-border)]', load.sourceType === 'UBER_FREIGHT' && 'hidden')}>
                        <div className="grid grid-cols-3 gap-1 mb-1">
                            <div>
                                <div className="text-[10px] text-[var(--lh-fg-muted)] mb-0.5 leading-none">Miles</div>
                                <input
                                    type="text"
                                    value={miles}
                                    onChange={handleInput(setMiles)}
                                    placeholder="-"
                                    className="w-full h-6 px-1.5 text-[11px] font-semibold text-[var(--lh-fg)] rounded border border-[var(--lh-border)] bg-transparent outline-none focus:border-[var(--lh-accent)] tabular-nums"
                                />
                            </div>
                            <div>
                                <div className="text-[10px] text-[var(--lh-fg-muted)] mb-0.5 leading-none">DH</div>
                                <input
                                    type="text"
                                    value={dh}
                                    onChange={handleInput(setDh)}
                                    placeholder="-"
                                    className="w-full h-6 px-1.5 text-[11px] font-semibold text-[var(--lh-fg)] rounded border border-[var(--lh-border)] bg-transparent outline-none focus:border-[var(--lh-accent)] tabular-nums"
                                />
                            </div>
                            <div>
                                <div className="text-[10px] text-[var(--lh-fg-muted)] mb-0.5 leading-none">Rate $</div>
                                <input
                                    type="text"
                                    value={rateStr}
                                    onChange={handleInput(setRateStr)}
                                    placeholder="-"
                                    className="w-full h-6 px-1.5 text-[11px] font-semibold text-[var(--lh-fg)] rounded border border-[var(--lh-border)] bg-transparent outline-none focus:border-[var(--lh-accent)] tabular-nums"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-1">
                            <div className="rounded border border-[var(--lh-border)] px-1.5 py-1">
                                <div className="text-[10px] text-[var(--lh-fg-muted)] leading-none">Total</div>
                                <div className="text-[11px] font-semibold text-[var(--lh-fg)] tabular-nums leading-tight">
                                    {totalWithDH > 0 ? Math.round(totalWithDH) : '-'}
                                </div>
                            </div>
                            <div className="rounded border border-[var(--lh-border)] px-1.5 py-1">
                                <div className="text-[10px] text-[var(--lh-fg-muted)] leading-none">RPM</div>
                                <div className="text-[11px] font-semibold text-[var(--lh-accent)] tabular-nums leading-tight">{rpm ? `$${rpm}` : '-'}</div>
                            </div>
                            <div className="rounded border border-[var(--lh-border)] px-1.5 py-1">
                                <div className="text-[10px] text-[var(--lh-fg-muted)] leading-none">RPM+DH</div>
                                <div className="text-[11px] font-semibold text-[var(--lh-accent)] tabular-nums leading-tight">
                                    {rpmWithDH ? `$${rpmWithDH}` : '-'}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}
